import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLock, faEye, faEyeSlash, faCheckCircle, faTimesCircle } from '@fortawesome/free-solid-svg-icons';
import './resetpassword.css';

const apiUrl = process.env.REACT_APP_API_URL;

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [token, setToken] = useState('');
  const [passwordStrength, setPasswordStrength] = useState({ score: 0, text: '', color: '' });

  useEffect(() => {
    const resetToken = searchParams.get('token');
    if (!resetToken) {
      setErrorMessage('Link invalid. Te rugăm să accesezi link-ul din emailul de resetare parolă.');
      setToken('');
    } else {
      setToken(resetToken);
      setErrorMessage('');
    }
  }, [searchParams]);

  const calculatePasswordStrength = (pass) => {
    let score = 0;
    if (pass.length >= 8) score++;
    if (/[A-Z]/.test(pass)) score++;
    if (/[a-z]/.test(pass)) score++;
    if (/[0-9]/.test(pass)) score++;
    if (/[!@#$%^&*(),.?":{}|<>]/.test(pass)) score++;
    
    if (score <= 2) return { score, text: 'Slabă', color: '#ff4444' };
    if (score === 3) return { score, text: 'Medie', color: '#ffaa00' };
    if (score === 4) return { score, text: 'Bună', color: '#00cc88' };
    return { score, text: 'Excelentă', color: '#00aa44' };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage('');
    setSuccessMessage('');

    if (password !== confirmPassword) {
      setErrorMessage('Parolele nu coincid!');
      return;
    }

    if (password.length < 8) {
      setErrorMessage('Parola trebuie să aibă cel puțin 8 caractere!');
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch(`${apiUrl}/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password }),
      });

      const data = await response.json();

      if (response.ok) {
        setSuccessMessage('Parola a fost resetată cu succes! Vei fi redirecționat către pagina de autentificare...');
        setTimeout(() => {
          navigate('/emailconnect');
        }, 3000);
      } else {
        setErrorMessage(data.message || 'A apărut o eroare. Te rugăm să încerci din nou.');
      }
    } catch (error) {
      setErrorMessage('A apărut o eroare la resetarea parolei. Te rugăm să încerci din nou.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="reset-password-container">
      <div className="reset-password-card">
        <h2 className="reset-password-title">Setează parolă nouă</h2>
        <p className="reset-password-description">
          Introdu noua parolă pentru contul tău.
        </p>

        {successMessage && (
          <div className="message-box success-message-box">
            <FontAwesomeIcon icon={faCheckCircle} className="message-icon" />
            {successMessage}
          </div>
        )}

        {errorMessage && (
          <div className="message-box error-message-box">
            <FontAwesomeIcon icon={faTimesCircle} className="message-icon" />
            {errorMessage}
          </div>
        )}

        {token && !successMessage && (
          <form className="reset-password-form" onSubmit={handleSubmit}>
            <div className="input-group-reg" style={{ position: 'relative' }}>
              <FontAwesomeIcon icon={faLock} className="input-icon-reg" />
              <input
                type={showPassword ? "text" : "password"}
                placeholder="Parolă nouă"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  if (e.target.value) {
                    setPasswordStrength(calculatePasswordStrength(e.target.value));
                  } else {
                    setPasswordStrength({ score: 0, text: '', color: '' });
                  }
                }}
                className="input-reg with-icon"
                style={{ paddingRight: '45px' }}
                required
              />
              <FontAwesomeIcon
                icon={showPassword ? faEyeSlash : faEye}
                className="toggle-password-icon-reset"
                onClick={() => setShowPassword(!showPassword)}
              />
            </div>

            {password && (
              <div className="password-strength-indicator">
                <div className="strength-bar">
                  <div 
                    className="strength-fill" 
                    style={{ 
                      width: `${(passwordStrength.score / 5) * 100}%`,
                      backgroundColor: passwordStrength.color 
                    }}
                  />
                </div>
                <span className="strength-text" style={{ color: passwordStrength.color }}>
                  Putere parolă: {passwordStrength.text}
                </span>
              </div>
            )}

            <div className="input-group-reg" style={{ position: 'relative' }}>
              <FontAwesomeIcon icon={faLock} className="input-icon-reg" />
              <input
                type={showConfirmPassword ? "text" : "password"}
                placeholder="Confirmă parola"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="input-reg with-icon"
                style={{ paddingRight: '45px' }}
                required
              />
              <FontAwesomeIcon
                icon={showConfirmPassword ? faEyeSlash : faEye}
                className="toggle-password-icon-reset"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              />
            </div>

            <button type="submit" className="input-reg-submit" disabled={isLoading || !token}>
              {isLoading ? 'Se resetează...' : 'Resetează parola'}
            </button>
          </form>
        )}

        <div className="reset-password-links">
          <Link to="/forgot-password" className="back-to-login">
            ← Solicită un nou link de resetare
          </Link>
          <Link to="/login" className="back-to-login" style={{ marginTop: '10px', display: 'block' }}>
            ← Înapoi la autentificare
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
